import { useState } from 'react';
import { useGoals, Goal } from '@/hooks/use-goals';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GoalCard } from '@/components/goals/GoalCard';
import { GoalModal } from '@/components/goals/GoalModal';
import {
  Plus,
  PiggyBank,
  Loader2,
  Target,
  Lock,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useSubscription } from '@/hooks/use-subscription';
import { UpgradePrompt } from '@/components/subscription/UpgradePrompt';

export default function Goals() {
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'complete'>('all');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { isWithinLimit, getLimit } = useSubscription();

  const {
    goals,
    loading: goalsLoading,
    createGoal,
    updateGoal,
    deleteGoal,
    addToGoal,
    withdrawFromGoal,
  } = useGoals();

  const goalsLocked = !isWithinLimit('max_goals', goals.length);
  const goalsLimit = getLimit('max_goals');

  const handleAddGoal = () => {
    if (goalsLocked) {
      setShowUpgrade(true);
      return;
    }
    setEditingGoal(null);
    setShowModal(true);
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setShowModal(true);
  };

  const handleSaveGoal = async (data: Partial<Goal>) => {
    if (editingGoal) {
      await updateGoal(editingGoal.id, data);
    } else {
      await createGoal(data);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      await deleteGoal(deleteId);
      setDeleteId(null);
    }
  };

  const filteredGoals = goals.filter((goal) => {
    const isComplete = goal.current_amount >= goal.target_amount;
    if (filter === 'active') return !isComplete && goal.is_active;
    if (filter === 'complete') return isComplete;
    return true;
  });

  const totalSaved = goals.reduce((sum, g) => sum + g.current_amount, 0);
  const totalTarget = goals.reduce((sum, g) => sum + g.target_amount, 0);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  if (goalsLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
            <PiggyBank className="h-6 w-6 text-primary-foreground" />
          </div>
          <h1 className="text-xl sm:text-2xl font-bold">Meus Cofrinhos</h1>
        </div>
        <div className="flex items-center gap-2">
          {goalsLimit !== null && (
            <span className="text-xs text-muted-foreground">
              {goals.length}/{goalsLimit}
            </span>
          )}
          <Button onClick={handleAddGoal}>
            {goalsLocked ? (
              <Lock className="h-4 w-4 sm:mr-2" />
            ) : (
              <Plus className="h-4 w-4 sm:mr-2" />
            )}
            <span className="hidden sm:inline">Novo Cofrinho</span>
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-6 text-primary-foreground">
          <div className="flex items-center gap-2 mb-2">
            <Target className="h-5 w-5 opacity-80" />
            <span className="text-sm opacity-90">Total Guardado</span>
          </div>
          <div className="text-2xl sm:text-3xl font-bold">{formatCurrency(totalSaved)}</div>
          <p className="text-sm opacity-80 mt-1">
            de {formatCurrency(totalTarget)} em metas
          </p>
        </div>
        <div className="bg-card rounded-xl p-6 border">
          <div className="flex items-center gap-2 mb-2">
            <PiggyBank className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Cofrinhos</span>
          </div>
          <div className="text-2xl sm:text-3xl font-bold">{goals.length}</div>
          <p className="text-sm text-muted-foreground mt-1">
            {goals.filter(g => g.current_amount >= g.target_amount).length} completos
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
        <TabsList>
          <TabsTrigger value="all">Todos ({goals.length})</TabsTrigger>
          <TabsTrigger value="active">
            Ativos ({goals.filter(g => g.is_active && g.current_amount < g.target_amount).length})
          </TabsTrigger>
          <TabsTrigger value="complete">
            Completos ({goals.filter(g => g.current_amount >= g.target_amount).length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-6">
          {filteredGoals.length === 0 ? (
            <div className="text-center py-12">
              <PiggyBank className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {filter === 'all'
                  ? 'Nenhum cofrinho ainda'
                  : filter === 'active'
                  ? 'Nenhum cofrinho ativo'
                  : 'Nenhum cofrinho completo'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {filter === 'all'
                  ? 'Crie seu primeiro cofrinho para começar a poupar!'
                  : 'Continue guardando para atingir suas metas!'}
              </p>
              {filter === 'all' && (
                <Button onClick={handleAddGoal}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Cofrinho
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredGoals.map((goal) => (
                <GoalCard
                  key={goal.id}
                  goal={goal}
                  onEdit={handleEditGoal}
                  onDelete={(id) => setDeleteId(id)}
                  onDeposit={(id, amount, accountId) => addToGoal(id, amount, accountId)}
                  onWithdraw={(id, amount, accountId) => withdrawFromGoal(id, amount, accountId)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal */}
      <GoalModal
        open={showModal}
        onOpenChange={setShowModal}
        goal={editingGoal}
        onSave={handleSaveGoal}
      />

      {/* Upgrade Dialog */}
      <Dialog open={showUpgrade} onOpenChange={setShowUpgrade}>
        <DialogContent className="sm:max-w-md">
          <UpgradePrompt feature="max_goals" />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cofrinho?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O cofrinho e todo o histórico serão removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
